import { motion } from "framer-motion";
import { fadeUp, imageReveal } from "@/lib/motion";

const credentials = [
  "Πτυχιούχος Ψυχολογίας",
  "Εκπαίδευση στη Γνωσιακή Συμπεριφορική Ψυχοθεραπεία (CBT)",
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 sm:py-32 relative"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Image */}
        <motion.div {...imageReveal} className="lg:col-span-5 order-2 lg:order-1 lg:sticky lg:top-28">
          <div className="relative aspect-[4/5] w-full max-w-[460px] mx-auto lg:mx-0">
            <div className="absolute inset-0 rounded-3xl overflow-hidden border border-border/40">
              <img
                src="https://customer-assets.emergentagent.com/job_therapy-anna-gr/artifacts/73it2gby_IMG_7564.jpeg"
                alt="Ο χώρος ψυχοθεραπείας της Άννας Παπαχρονόπουλου στην Πάτρα — γωνιά ηρεμίας με φυτά"
                data-testid="about-image"
                loading="lazy"
                width="460"
                height="575"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-card border border-border/50 rounded-2xl px-5 py-4 shadow-sm">
              <p className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                Έδρα
              </p>
              <p className="font-heading text-xl text-foreground mt-1">
                Ρήγα Φεραίου 119
              </p>
              <p className="text-sm text-muted-foreground font-body">Πάτρα</p>
            </div>
          </div>
        </motion.div>

        {/* Text */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <motion.p
            {...fadeUp}
            className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5"
          >
            Λίγα λόγια για εμένα
          </motion.p>

          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 }}
            data-testid="about-heading"
            className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]"
          >
            Ας <span className="italic font-light">γνωριστούμε.</span>
          </motion.h2>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 space-y-5 max-w-[620px] font-body font-light text-lg text-foreground/75 leading-relaxed"
          >
            <p>
              Ασχολούμαι με την ψυχολογία γιατί πιστεύω βαθιά πως καθένας μας
              αξίζει να κατανοήσει τον εαυτό του χωρίς ντροπή και χωρίς κρίση.
              Μέσα από τη δική μου διαδρομή — προσωπική και επιστημονική — έμαθα
              πως η αλλαγή γίνεται όταν νιώθουμε αρκετά ασφαλείς ώστε να την
              αφήσουμε να συμβεί.
            </p>
            <p>
              Στη δουλειά μου χρησιμοποιώ τη{" "}
              <span className="text-foreground">
                Γνωσιακή Συμπεριφορική Ψυχοθεραπεία (CBT)
              </span>{" "}
              — μια από τις πιο τεκμηριωμένες προσεγγίσεις παγκοσμίως. Αυτό που
              αγαπώ σε αυτήν είναι ότι δεν σταματά στη συζήτηση: σου δίνει
              πραγματικά εργαλεία για το άγχος, τις σχέσεις, την αυτοεκτίμηση,
              το OCD, το τραύμα και τη συναισθηματική δυσφορία.
            </p>
            <p>
              Στο γραφείο μου στη Ρήγα Φεραίου 119, στην Πάτρα — αλλά και online
              — προσπαθώ να φτιάξουμε μαζί έναν χώρο όπου μπορείς να μιλήσεις
              ελεύθερα, να ακουστείς πραγματικά, και σιγά-σιγά να ξαναβρείς τη
              σχέση με τον εαυτό σου.
            </p>
            <p className="font-italic-heading text-2xl text-foreground/85 italic pl-5 border-l-2 border-primary/60 leading-snug">
              «Psyloveyourself» — η ιδέα ότι μπορούμε να μάθουμε να συνυπάρχουμε
              με τον εαυτό μας με κατανόηση, αποδοχή και φροντίδα.
            </p>
            <p className="text-foreground/75">
              Αν αναρωτιέσαι αν αυτό που νιώθεις «αξίζει» θεραπεία — αξίζει.
              Είμαι εδώ για να το δούμε μαζί.
            </p>
          </motion.div>

          {/* Credentials chips */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-2.5"
            data-testid="about-credentials"
          >
            {credentials.map((c) => (
              <span
                key={c}
                className="inline-flex items-center text-sm font-body text-foreground/80 bg-card border border-border/60 rounded-full px-4 py-2"
              >
                {c}
              </span>
            ))}
          </motion.div>

          {/* Stats */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-[520px]"
          >
            {[
              { k: "Προσέγγιση", v: "CBT" },
              { k: "Συνεδρίες", v: "50′" },
              { k: "Μορφή", v: "Δια ζώσης / Online" },
            ].map((s) => (
              <div key={s.k} className="border-t border-border pt-4">
                <p className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                  {s.k}
                </p>
                <p className="font-heading text-xl text-foreground mt-1">
                  {s.v}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
